import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'

import {
    getSupplierColumns,
    getSupplierFilterItems,
    getSupplierFilterToggles,
} from '../components/Suppliers/supplier-list-config'
import { getSuppliers } from '../services'
import { paths } from '@/utils/path'

interface SupplierListFilters {
    is_active: string
}

const FILTER_DEFAULTS: SupplierListFilters = {
    is_active: '',
}

export const useSupplierList = () => {
    const navigate = useNavigate()

    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<SupplierListFilters>({
        defaults: { sort_by: 'created_at', sort_order: 'desc' },
        filterDefaults: FILTER_DEFAULTS,
    })

    const { control } = useForm<SupplierListFilters>({
        defaultValues: filters,
    })

    const fetchFn = useCallback(() => getSuppliers(queryParams), [queryParams])

    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_SUPPLIERS, queryParams],
        queryFn: fetchFn,
        placeholderData: true,
    })

    const columns = useMemo(() => getSupplierColumns(), [])
    const filterItems = useMemo(() => getSupplierFilterItems(), [])
    const filterToggles = useMemo(() => getSupplierFilterToggles(), [])

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    const handleCreate = useCallback(
        () => navigate(paths.warehouseSupplierCreate),
        [navigate]
    )

    return {
        suppliers: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        control,
        setFilter,
        params,
        searchInputProps,

        columns,
        filterItems,
        filterToggles,

        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        handleCreate,
        refetch,
    }
}
